import React, { useEffect, useMemo, useRef, useState } from "react";
import BX from './img/boxing.jpg'
import K1 from './img/k1.jpg'
import MMA from './img/MMA.jpg'
import FT from './img/Strength&Mobility.jpg'

/**
 * Reusable slider for showcasing classes/activities with photo + text.
 *
 * Props:
 *  - slides: Array<{ id?: string|number, title: string, subtitle?: string, description: string, image: string }>
 *  - autoplay?: boolean (default true)
 *  - interval?: number ms between slides (default 6000)
 *  - showDots?: boolean (default true)
 *  - className?: string (optional root class)
 *
 * Keyboard: ← → to navigate
 * Touch/Drag: swipe to navigate
 * "Read more": description collapses to 3 lines with toggle
 */
export default function ClassesSlider({
  slides,
  autoplay = true,
  interval = 6000,
  showDots = true,
  className = "",
}) {
  const sampleSlides = useMemo(
    () =>
      [
        {
          id: 1,
          title: "K-1 Kikbokss",
          subtitle: "K-1(Ring)",
          description:
            "K-1 — tā ir stingrākā un universālākā kikboksa disciplīna. Tā apvieno sevī boksa, muay thai un klasiskā kikboksa elementus, padarot cīņas par maksimāli iespaidīgām un skatāmām.",
          image: K1,
        },
        {
          id: 2,
          title: "Bokss",
          subtitle: "Kustība, aizsardzība, precizitāte",
          description:
            "Mācāmies klasiskā boksa pamatus: stāju, soli, kombinācijas, darbību ringā un ārpus tā. Lieliski attīsta koordināciju un izturību.",
          image: BX,
        },
        {
          id: 3,
          title: "MMA",
          // subtitle: "Spēles, kustību prieks un drošība",
          description:
            "Sacensību cīņas sports, kurā atļautas gan sitienu disciplīnu (boks, muai-thai, kikbokss) tehnikas, gan cīņas / grīdas maču skolas (brīvā/greko-romiešu cīņa, džudo, sambo, BJJ) paņēmieni. Cīņa notiek cage (būris) vai ringā un ietver stājas (stand-up), klincu (clinč) un parteru (ground). Mērķis — uzvarēt ar nokautu / tehnisko nokautu (TKO), ar sāpju vai žņaugšanas paņēmienu, vai arī pēc tiesnešu lēmuma.",
           image: MMA,
        },
        {
          id: 4,
          title: "Fitness",
          subtitle: "Spēles, kustību prieks un drošība",
          description:
            "Fitness — tā ir vingrinājumu un aktivitāšu sistēma, kas vērsta uz fiziskās formas, veselības un pašsajūtas uzlabošanu. Nodarbībās tiek iekļauti kardio, spēka un funkcionālie treniņi, kas attīsta izturību, spēku, lokanību un koordināciju. Fitnesa nodarbības ir piemērotas jebkuram vecumam un sagatavotības līmenim, tās palīdz uzturēt veselīgu dzīvesveidu un nodrošina labu garastāvokli.",
           image: FT,
        },
      ],
    []
  );

  const data = slides?.length ? slides : sampleSlides;

  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const isDragging = useRef(false);

  // Clamp index if slides length changes
  useEffect(() => {
    if (index >= data.length) setIndex(0);
  }, [data.length, index]);

  // Autoplay
  useEffect(() => {
    if (!autoplay || data.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % data.length);
      setExpanded(false);
    }, Math.max(2500, interval));
    return () => clearInterval(timerRef.current);
  }, [autoplay, interval, data.length]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const prev = () => {
    setIndex((i) => (i - 1 + data.length) % data.length);
    setExpanded(false);
  };
  const next = () => {
    setIndex((i) => (i + 1) % data.length);
    setExpanded(false);
  };

  // Touch/drag handlers
  const onTouchStart = (e) => {
    isDragging.current = true;
    touchStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
    touchDeltaX.current = 0;
    clearInterval(timerRef.current);
  };
  const onTouchMove = (e) => {
    if (!isDragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    touchDeltaX.current = x - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (!isDragging.current) return;
    const threshold = 60; // px
    if (touchDeltaX.current > threshold) prev();
    else if (touchDeltaX.current < -threshold) next();
    isDragging.current = false;
  };

  const current = data[index];

  return (
    <div className={`fc-classes-slider ${className}`}>
      <style>{css}</style>

      <div
        className="slide"
        onMouseDown={onTouchStart}
        onMouseMove={onTouchMove}
        onMouseUp={onTouchEnd}
        onMouseLeave={onTouchEnd}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img className="bg" src={current.image} alt={current.title} />
        <div className="overlay" />

        <div className="content">
          {current.subtitle && <div className="subtitle">{current.subtitle}</div>}
          <h3 className="title">{current.title}</h3>

          <p className={`desc ${expanded ? "expanded" : "collapsed"}`}>
            {current.description}
          </p>

          <button className="toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Mazāk" : "Vairāk"}
          </button>

          <div className="controls">
            <button className="arrow" aria-label="Previous" onClick={prev}>
              ‹
            </button>
            <button className="arrow" aria-label="Next" onClick={next}>
              ›
            </button>
          </div>
        </div>
      </div>

      {showDots && (
        <div className="dots" role="tablist" aria-label="Slides">
          {data.map((s, i) => (
            <button
              key={s.id ?? i}
              role="tab"
              aria-selected={i === index}
              className={`dot ${i === index ? "active" : ""}`}
              onClick={() => {
                setIndex(i);
                setExpanded(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const css = `
  .fc-classes-slider { position: relative; width: 100%; max-width: 1100px; margin: 0 auto; }
  .fc-classes-slider .slide { position: relative; width: 100%; aspect-ratio: 16 / 9; overflow: hidden; border-radius: 18px; box-shadow: 0 10px 30px rgba(0,0,0,.25); }
  .fc-classes-slider .bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transform: scale(1.02); }
  .fc-classes-slider .overlay { position: absolute; inset: 0; background: linear-gradient( to right, rgba(0,0,0,.55) 0%, rgba(0,0,0,.45) 35%, rgba(0,0,0,.15) 70%, rgba(0,0,0,.05) 100% ); }

  .fc-classes-slider .content { position: absolute; inset: 0; display: grid; align-content: center; gap: .75rem; padding: clamp(16px, 3vw, 36px); color: #fff; max-width: min(680px, 70%); }
  .fc-classes-slider .subtitle { font-size: clamp(.85rem, 1.4vw, 1rem); letter-spacing: .04em; text-transform: uppercase; opacity: .9; }
  .fc-classes-slider .title { font-size: clamp(1.4rem, 3.6vw, 2.2rem); margin: 0; line-height: 1.15; }

  .fc-classes-slider .desc { font-size: clamp(.95rem, 1.6vw, 1.05rem); line-height: 1.45; opacity: .95; margin: .25rem 0 .5rem; }
  .fc-classes-slider .desc.collapsed { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .fc-classes-slider .desc.expanded { display: block; }

  .fc-classes-slider .toggle { appearance: none; border: 0; background: rgba(255,255,255,.14); color: #fff; padding: .5rem .9rem; border-radius: 999px; cursor: pointer; font-weight: 600; backdrop-filter: blur(3px); }
  .fc-classes-slider .toggle:hover { background: rgba(255,255,255,.24); }

  .fc-classes-slider .controls { position: absolute; right: clamp(8px, 2vw, 16px); bottom: clamp(8px, 2vw, 16px); display: flex; gap: .5rem; }
  .fc-classes-slider .arrow { width: 44px; height: 44px; border-radius: 999px; border: 0; background: rgba(255,255,255,.18); color: #fff; font-size: 28px; line-height: 1; cursor: pointer; }
  .fc-classes-slider .arrow:hover { background: rgba(255,255,255,.28); }

  .fc-classes-slider .dots { display: flex; gap: .4rem; justify-content: center; margin-top: .7rem; }
  .fc-classes-slider .dot { width: 9px; height: 9px; border-radius: 999px; border: 0; background: rgba(255,255,255,.35); cursor: pointer; }
  .fc-classes-slider .dot.active { background: #fff; }

  @media (max-width: 820px) {
    .fc-classes-slider .content { max-width: 100%; padding-right: clamp(16px, 4vw, 28px); }
    .fc-classes-slider .overlay { background: linear-gradient( to top, rgba(0,0,0,.65) 0%, rgba(0,0,0,.45) 40%, rgba(0,0,0,.1) 90% ); }
    .fc-classes-slider .controls { right: 10px; bottom: 10px; }
  }
    /* ДОБАВЬ в css вашего компонента */
.fc-classes-slider .desc {
  overflow-wrap: anywhere;   /* перенос длинных слов */
  word-break: break-word;
  hyphens: auto;
}

/* планшеты */
@media (max-width: 820px) {
  .fc-classes-slider .content {
    max-width: 100%;
    padding-right: clamp(16px, 4vw, 28px);
    gap: .45rem;
  }
  .fc-classes-slider .title { font-size: clamp(1.2rem, 4.6vw, 1.6rem); }
  .fc-classes-slider .subtitle { font-size: clamp(.75rem, 2.7vw, .95rem); }
  .fc-classes-slider .desc { font-size: clamp(.85rem, 2.8vw, .95rem); }
  .fc-classes-slider .desc.collapsed { -webkit-line-clamp: 3; } /* 3 строки */
}

/* телефоны */
@media (max-width: 480px) {
  .fc-classes-slider .slide { aspect-ratio: 1 / 1; border-radius: 14px; }
  .fc-classes-slider .content { padding: 14px; gap: .35rem; }
  .fc-classes-slider .title { font-size: clamp(1.05rem, 6vw, 1.35rem); }
  .fc-classes-slider .subtitle { font-size: clamp(.7rem, 3.5vw, .85rem); }
  .fc-classes-slider .desc { font-size: clamp(.8rem, 3.8vw, .9rem); line-height: 1.35; }
  .fc-classes-slider .desc.collapsed { -webkit-line-clamp: 2; } /* 2 строки на очень узких */
  .fc-classes-slider .toggle { padding: 6px 10px; font-size: .85rem; }
  .fc-classes-slider .arrow { width: 36px; height: 36px; font-size: 22px; }
}

/* экстрамаленькие устройства */
@media (max-width: 360px) {
  .fc-classes-slider .desc.collapsed { -webkit-line-clamp: 2; }
}

`;

// --- Example usage ---
export function DemoClassesSlider() {
  const slides = [
    {
      id: "kb",
      title: "Kikbokss (pieaugušie)",
      subtitle: "Spēks • Izturība • Tehnika",
      description:
        "Klasiskie sitieni, kondīcija, paņēmieni un drošs sparings trenera uzraudzībā. Lieliski der kā vispārējai attīstībai, tā arī sacensību sagatavošanai.",
      image:
        "https://images.unsplash.com/photo-1549055240-489e7d5c1f6a?q=80&w=1600&auto=format&fit=crop",
    },
    {
      id: "bx",
      title: "Bokss (grupa)",
      subtitle: "Kustība un aizsardzība",
      description:
        "Attīstām roku darbu, kāju soli un galvas kustību. Treniņi piemēroti gan iesācējiem, gan pieredzējušiem sportistiem.",
      image:
        "https://images.unsplash.com/photo-1540148426945-4dc21f2ad1d4?q=80&w=1600&auto=format&fit=crop",
    },
    {
      id: "kids",
      title: "Bērnu treniņi (no 3+)",
      subtitle: "Draudzīgi un droši",
      description:
        "Spēļu elementi, kustību koordinācija, pamata tehnika un disciplīna. Mērķis – veselīgs paradums kustēties ar prieku.",
      image:
        "https://images.unsplash.com/photo-1544665163-206a3fbdc0e2?q=80&w=1600&auto=format&fit=crop",
    },
  ];

  return <ClassesSlider slides={slides} autoplay interval={7000} />;
}
